import './Search.less';
import { useAppSelector } from '@hooks/useStore';
import { useLazyGetNamespacesQuery, useLazySearchDocumentsQuery } from '@redux/api/adminApi';
import { Input } from '@sberlab/core-components';
import { IconDocumentFile20, IconSearchFind20 } from '@sberlab/core-icons';
import { ISearchDocument } from '@typings/rootSlice';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

interface SearchProps {
    className?: string;
}

const Search: React.FC<SearchProps> = ({ className = '' }) => {
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState<ISearchDocument[]>([]);
    const [triggerSearch] = useLazySearchDocumentsQuery();
    const [triggerGetNamespaces] = useLazyGetNamespacesQuery();
    const debounceRef = useRef<NodeJS.Timeout>();
    const containerRef = useRef<HTMLDivElement>(null);
    const namespaceMap = useAppSelector(state => state.documentSlice.namespaceMap);

    const buildUrl = useCallback((doc: ISearchDocument) => {
        const ns = namespaceMap[doc.namespace_id];
        return `/${ns || 'help'}/${doc.title_for_url}`;
    }, [namespaceMap]);

    const handleSearch = useCallback(async (searchQuery: string) => {
        if (searchQuery.trim().length > 0) {
            if (Object.keys(namespaceMap).length === 0) {
                triggerGetNamespaces();
            }
            const result = await triggerSearch({ search: searchQuery }).unwrap();
            setSearchResults(result.documents);
        } else {
            setSearchResults([]);
        }
    }, [triggerSearch, namespaceMap, triggerGetNamespaces]);

    const debouncedSearch = useCallback((searchQuery: string) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            handleSearch(searchQuery);
        }, 500);
    }, [handleSearch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);
        debouncedSearch(value);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setSearchResults([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="search__container" ref={containerRef}>
            <Input
                className='search'
                placeholder='Что вы хотите найти?'
                suffixIcon={<IconSearchFind20 size={20} />}
                value={query}
                onChange={handleInputChange}
            />
            {searchResults.length > 0 && (
                <div className={`search__results ${className}`}>
                    {searchResults.map((doc) => (
                        <Link
                            key={doc.document_id}
                            to={buildUrl(doc)}
                            className="search__results__item"
                            onClick={() => setSearchResults([])}
                        >
                            <IconDocumentFile20 />
                            {doc.title}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Search;
